/**
 * The Finance → Salaries per-cycle payouts grid's filter + sort declaration — a
 * consumer of the generic filter toolkit (`components/filters`), mirroring
 * `feesPaymentsFilterConfig` / `claimsFilterConfig`.
 *
 * IMPORTANT: the SELECTED CYCLE is NOT a facet. The page keeps the
 * `CycleSidebar` / `CycleSelect` picker (local `selectedCycleId` state) as the
 * primary selector and merges `{ cycleId: selectedCycleId, ...apiParams }` into
 * the payments query. This config only declares the facets that filter WITHIN
 * the chosen cycle.
 *
 * There is NO search facet: the salaries payments endpoint has no `q` contract.
 *
 * Facets exposed:
 *   - status  → `status`                  multi-select over pending/paid/held.
 *                                          The page's prior default was the "All"
 *                                          chip and the backend treats an
 *                                          *omitted* status as "all" — so the
 *                                          default value is `[]` and "All" is
 *                                          encoded by OMITTING the param
 *                                          (`allEncoding: 'omit'`).
 *   - role    → `role`                     multi-select over teacher/manager;
 *                                          allEncoding 'omit'.
 *   - payout  → `payoutFrom`/`payoutTo`    date range over the recorded paid date.
 *
 * Sorts map to the backend's allow-listed `sort` fields ({paidAt, netPaise}).
 * The default (first entry) is "Paid date (newest)" = paidAt:desc.
 *
 * Sortable column header: Paid (paidAt). BOTH directions are registered so the
 * header can toggle desc↔asc without snapping back to the default sort. amount
 * (netPaise) stays dropdown-only.
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const salaryPaymentsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Prior page default was the "All" chip. The backend reads an omitted
      // status as "all", so the default value is empty AND "All" is encoded by
      // omitting the param (no explicit `status=all`).
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
        { value: 'held', label: 'Held' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'role',
      label: 'Role',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'teacher', label: 'Teacher' },
        { value: 'manager', label: 'Nalim' },
      ],
    },
    {
      kind: 'date-range',
      key: 'payout',
      label: 'Payout',
      group: 'filters',
      params: { from: 'payoutFrom', to: 'payoutTo' },
    },
  ],
  sorts: [
    { value: 'paidAt:desc', label: 'Paid date (newest)' },
    // Registered so the Paid column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'paidAt:asc', label: 'Paid date (oldest)' },
    { value: 'netPaise:desc', label: 'Amount (high → low)' },
  ],
  resultNoun: ['payment', 'payments'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. Only the Paid
 * column is bound: it sorts on `paidAt`, the field with both directions
 * registered above (amount stays dropdown-only).
 */
export const SALARY_PAYMENTS_SORT_COLUMNS: Record<'paidAt', SortColumnBinding> = {
  paidAt: { field: 'paidAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Salaries payments grid, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/finance/salaries` opens on the configured defaults).
 *
 * The selected cycle is NOT a toolkit facet, so it is not declared here — it is
 * local component state (the cycle picker), not a URL-synced filter facet.
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `payout` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `payoutFrom`/`payoutTo`;
 * omitting it would stop a preset choice from sticking. Kept beside the config
 * (not in the route) so this boundary is unit-testable without loading the
 * route's React/router side effects.
 */
export const salaryPaymentsSearchSchema = z.object({
  status: z.string().optional(),
  role: z.string().optional(),
  payout: z.string().optional(),
  payoutFrom: z.string().optional(),
  payoutTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
